import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const UPDATED = "5 February 2026";

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-accent transition-colors"
        data-testid="terms-back"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>
      <p className="mt-8 text-primary font-semibold uppercase tracking-wider text-xs">Legal</p>
      <h1 className="font-display font-bold text-4xl md:text-5xl text-accent mt-2 tracking-tight">
        Terms of service
      </h1>
      <p className="text-sm text-muted-foreground mt-3">Last updated: {UPDATED}</p>

      <div className="prose-fr mt-10 space-y-8 text-accent/85 leading-relaxed">
        <Section title="1. Who we are">
          <p>
            Fetchright ("we", "us", "our") operates the Fetchright dog-breed matching platform available at
            fetchright.net and via our companion mobile app. Fetchright is provided by an Australian sole trader.
            Contact: <a className="text-primary underline" href="mailto:support@fetchright.net">support@fetchright.net</a>.
          </p>
        </Section>

        <Section title="2. What Fetchright does">
          <p>
            We provide informational content: a lifestyle-based breed quiz, breed profiles, care guides,
            cost calculators, a directory of Australian shelters and breeders, an AI-powered advisor,
            and a feed of dogs currently available for adoption sourced from third-party shelters.
          </p>
          <p className="mt-3 font-semibold text-accent">
            Fetchright is guidance only. It is not veterinary, behavioural, legal, or financial advice.
            Always consult a licensed veterinarian, trainer, or professional before making decisions
            about a specific dog.
          </p>
        </Section>

        <Section title="3. No account needed">
          <p>
            Fetchright does not require an account or sign-in. Your saved favourites, quiz results and
            the $2 Unlock are tied to the device and browser you use. If you clear your browser data or
            switch devices, your saves and unlock will not carry over — contact support if you need your
            unlock restored.
          </p>
        </Section>

        <Section title="4. Fetchright Unlock (one-time payment)">
          <p>
            Fetchright Unlock is a <strong>one-time payment of AUD $2</strong> that grants
            lifetime access to your full quiz results (all ranked breed matches, not just the
            top one), unlimited saved breeds, suburb-level cost detail, and priority AI advisor
            responses. Pricing shown at{" "}
            <Link to="/upgrade" className="text-primary underline">fetchright.net/upgrade</Link>{" "}
            is in Australian dollars, inclusive of GST where applicable, and processed by our
            payments provider (Stripe Payments Australia Pty Ltd).
          </p>
          <p className="mt-3">
            There is no subscription. See our{" "}
            <Link to="/refunds" className="text-primary underline">Refunds policy</Link> for
            refund conditions.
          </p>
        </Section>

        <Section title="5. Changes to Fetchright Unlock pricing">
          <p>
            The Unlock price shown at{" "}
            <Link to="/upgrade" className="text-primary underline">fetchright.net/upgrade</Link>{" "}
            may change over time. Any change applies only to <strong>new</strong> purchases —
            existing unlocked users keep lifetime access at whatever they paid.
          </p>
        </Section>

        <Section title="6. Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1.5">
            <li>Scrape, mirror, or systematically copy Fetchright content without written permission.</li>
            <li>Attempt to circumvent authentication or the Fetchright Unlock paywall.</li>
            <li>Use Fetchright to harass, defame, or misrepresent any breeder, shelter, or individual.</li>
            <li>Upload malicious code or attempt to disrupt the service.</li>
          </ul>
        </Section>

        <Section title="7. Third-party content">
          <p>
            Shelter, trainer, breeder, and adoptable-dog listings on Fetchright come from third-party
            sources (including PetRescue.com.au, state breeder registries, and individual
            organisations). We do not verify each listing and are not responsible for the accuracy of
            third-party information or the outcome of any interaction with those organisations.
          </p>
        </Section>

        <Section title="8. Australian Consumer Law">
          <p>
            Nothing in these terms excludes, restricts, or modifies any consumer guarantee, right, or
            remedy you have under the Australian Consumer Law that cannot be lawfully excluded. Where our
            liability can be limited, our total liability to you for any claim relating to Fetchright is
            limited to the fees you paid us in the 12 months before the claim arose.
          </p>
        </Section>

        <Section title="9. Changes and termination">
          <p>
            We may update these terms with reasonable notice by posting the new version at{" "}
            <Link to="/terms" className="text-primary underline">fetchright.net/terms</Link>. Material changes
            to Fetchright Unlock pricing will be notified on the website at least 14 days before taking effect.
            We may suspend or terminate access for use that violates these terms.
          </p>
        </Section>

        <Section title="10. Governing law">
          <p>
            These terms are governed by the laws of Victoria, Australia. You and Fetchright submit to the
            non-exclusive jurisdiction of the courts of Victoria for any dispute.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            Questions? Email{" "}
            <a className="text-primary underline" href="mailto:support@fetchright.net">
              support@fetchright.net
            </a>
            . We aim to respond within 2 business days.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl text-accent mb-3">{title}</h2>
      <div className="text-[15px]">{children}</div>
    </section>
  );
}
